import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const articleImages: Record<string, string> = {
  'diyibanzhu-zhize-quanmian-jiexi': 'https://readdy.ai/api/search-image?query=Professional%20minimalist%20office%20workspace%20with%20warm%20lighting%20and%20clean%20composition%2C%20modern%20tech%20aesthetic%2C%20soft%20cream%20and%20beige%20tones%2C%20abstract%20geometric%20elements%2C%20editorial%20style%20photography&width=800&height=400&seq=article-zhize-01&orientation=landscape',
  'diyibanzhu-quanxian-tixi-xiangjie': 'https://readdy.ai/api/search-image?query=Clean%20modern%20workspace%20with%20shield%20icon%20concept%2C%20warm%20beige%20background%2C%20soft%20natural%20lighting%2C%20minimalist%20professional%20photography%2C%20abstract%20security%20theme&width=800&height=400&seq=article-quanxian-01&orientation=landscape',
  'diyibanzhu-renming-biaozhun-yu-liucheng': 'https://readdy.ai/api/search-image?query=Modern%20office%20with%20certificate%20and%20checklist%20concept%2C%20warm%20cream%20tones%2C%20professional%20atmosphere%2C%20clean%20minimalist%20composition%2C%20soft%20natural%20light%2C%20editorial%20style&width=800&height=400&seq=article-renming-01&orientation=landscape',
  'shequ-neirong-guanli-shiyong-jiqiao': 'https://readdy.ai/api/search-image?query=Organized%20desk%20with%20documents%20and%20laptop%2C%20warm%20beige%20background%2C%20modern%20minimalist%20workspace%2C%20soft%20lighting%2C%20professional%20editorial%20style%2C%20clean%20composition&width=800&height=400&seq=article-neirong-01&orientation=landscape',
  'banzhu-tuandui-jianshe-yu-guanli': 'https://readdy.ai/api/search-image?query=Team%20collaboration%20abstract%20concept%2C%20warm%20cream%20tones%2C%20modern%20minimalist%20office%20atmosphere%2C%20soft%20natural%20lighting%2C%20professional%20editorial%20photography%2C%20clean%20geometric%20composition&width=800&height=400&seq=article-team-01&orientation=landscape',
  'shequ-wenhua-jianshe-celve': 'https://readdy.ai/api/search-image?query=Community%20gathering%20abstract%20concept%20with%20connected%20circles%2C%20warm%20cream%20background%2C%20modern%20minimalist%20design%2C%20soft%20lighting%2C%20harmonious%20composition%2C%20editorial%20style&width=800&height=400&seq=article-wenhua-01&orientation=landscape',
  'yonghu-chengchang-tixi-sheji': 'https://readdy.ai/api/search-image?query=Growth%20chart%20abstract%20concept%2C%20warm%20golden%20tones%2C%20minimalist%20professional%20design%2C%20clean%20composition%2C%20soft%20natural%20lighting%2C%20editorial%20style%20photography&width=800&height=400&seq=article-growth-01&orientation=landscape',
  'cong-putong-banzhu-dao-diyibanzhu': 'https://readdy.ai/api/search-image?query=Career%20growth%20staircase%20concept%2C%20warm%20golden%20tones%2C%20minimalist%20professional%20atmosphere%2C%20clean%20geometric%20design%2C%20soft%20natural%20lighting%2C%20editorial%20photography&width=800&height=400&seq=article-jinsheng-01&orientation=landscape',
  'chenggong-ban-kuai-anli-jiexi': 'https://readdy.ai/api/search-image?query=Success%20case%20study%20abstract%20concept%2C%20warm%20beige%20background%2C%20modern%20minimalist%20design%2C%20clean%20geometric%20shapes%2C%20professional%20editorial%20photography%2C%20soft%20natural%20light&width=800&height=400&seq=article-case-01&orientation=landscape',
  'weiji-chuli-yu-yuqing-guanli': 'https://readdy.ai/api/search-image?query=Crisis%20management%20abstract%20concept%2C%20warm%20cream%20tones%2C%20shield%20and%20alert%20theme%2C%20minimalist%20professional%20design%2C%20soft%20lighting%2C%20editorial%20photography&width=800&height=400&seq=article-weiji-01&orientation=landscape',
};

const categoryJsonLd = (categoryName: string, categoryDesc: string) => ({
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  name: `${categoryName} - 第一版主`,
  description: categoryDesc,
  url: `https://diyibanzhu.com/category/${categoryName}`,
  isPartOf: {
    '@type': 'WebSite',
    name: '第一版主',
    url: 'https://diyibanzhu.com/',
  },
});

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [selectedSlug, setSelectedSlug] = useState(slug || categories[0].slug);

  useEffect(() => {
    if (slug) {
      setSelectedSlug(slug);
    }
  }, [slug]);

  const currentCategory = categories.find((c) => c.slug === selectedSlug) || categories[0];
  const categoryArticles = articles.filter((a) => a.categorySlug === selectedSlug);

  return (
    <>
      <SEOHead
        title={`${currentCategory.name} - 第一版主 | 论坛版块管理核心指南`}
        description={currentCategory.description}
        keywords={`第一版主,${currentCategory.name},论坛版主管理,社区版块管理,论坛运营`}
        canonicalUrl={`https://diyibanzhu.com/category/${selectedSlug}`}
        ogTitle={`${currentCategory.name} - 第一版主`}
        ogDescription={currentCategory.description}
        ogUrl={`https://diyibanzhu.com/category/${selectedSlug}`}
        jsonLdScripts={[categoryJsonLd(currentCategory.name, currentCategory.description)]}
      />

      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: currentCategory.name },
        ]}
      />

      <section className="w-full py-8 md:py-12 bg-background-50">
        <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
              {currentCategory.name}
            </h1>
            <p className="text-sm md:text-base text-foreground-500 max-w-2xl">
              {currentCategory.description}
            </p>
          </div>

          <div className="flex items-center gap-2 mb-10 overflow-x-auto pb-2 flex-wrap">
            {categories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setSelectedSlug(cat.slug)}
                className={`px-4 py-2 text-sm font-medium rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                  selectedSlug === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-800'
                }`}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
            {categoryArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-300 transition-all duration-300 cursor-pointer"
              >
                <div className="relative w-full h-44 overflow-hidden">
                  <img
                    src={articleImages[article.slug] || articleImages['diyibanzhu-zhize-quanmian-jiexi']}
                    alt={article.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    {article.tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 text-xs rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                  <h2 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-2 line-clamp-2 group-hover:text-primary-600 transition-colors">
                    {article.title}
                  </h2>
                  <p className="text-xs md:text-sm text-foreground-500 line-clamp-2 mb-3">
                    {article.excerpt}
                  </p>
                  <div className="flex items-center justify-between text-xs text-foreground-400">
                    <span>{article.date}</span>
                    <span>{article.readTime} 分钟阅读</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>

          {categoryArticles.length === 0 && (
            <div className="text-center py-16">
              <p className="text-foreground-400 text-sm">该分类暂无文章</p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}